import { h, inject, provide, ref } from 'vue';

import { CdsListItem, CdsListGroup } from '../';

import type { IInternalItem } from '@components/composable';
import type { InjectionKey, Ref, SetupContext } from 'vue';

export const ListKey: InjectionKey<{ hasPrepend: Ref<boolean>; updateHasPrepend: (value: boolean) => void }> =
  Symbol.for('cds:list');

export function createList() {
  const parent = inject(ListKey, { hasPrepend: ref(false), updateHasPrepend: () => null });
  const data = {
    hasPrepend: ref(false),
    updateHasPrepend: (value: boolean) => {
      if (value) data.hasPrepend.value = value;
    }
  };

  provide(ListKey, data);

  return parent;
}

export function createChildren(props: { tag: string; items: IInternalItem[] }, context: SetupContext<any[]>): any {
  return () =>
    context.slots.default?.()
      ? { default: context.slots.default }
      : props.items.map(({ children, props: itemProps, raw: item }) => {
          const slotsWithItem = {
            default: context.slots.default
              ? (slotProps: any) => context.slots.default?.({ ...slotProps, item })
              : undefined,
            prepend: context.slots.prepend
              ? (slotProps: any) => context.slots.prepend?.({ ...slotProps, item })
              : undefined,
            append: context.slots.append
              ? (slotProps: any) => context.slots.append?.({ ...slotProps, item })
              : undefined,
            title: context.slots.title ? (slotProps: any) => context.slots.title?.({ ...slotProps, item }) : undefined,
            description: context.slots.description
              ? (slotProps: any) => context.slots.description?.({ ...slotProps, item })
              : undefined
          };

          return children?.length
            ? h(
                CdsListGroup,
                { value: itemProps?.value },
                {
                  activator: ({ props: activatorProps }: any) =>
                    h(CdsListItem, { ...itemProps, ...activatorProps }, slotsWithItem),
                  default: () => createChildren({ ...props, items: children }, context)()
                }
              )
            : h(CdsListItem, itemProps, slotsWithItem);
        });
}

export function useList() {
  return inject(ListKey, null);
}
